/**
 * @module utils/urls
 * @deprecated Use window.URLSearchParams polyfill instead.
 */
import { hasNativeDataType, removeInnerQuotes } from "./utils";
import isNil from "lodash/isNil";

let _cachedUrlParams = null;
let _cachedUrl = null;

/**
 * @method getUrlVars
 * @description returns a map of query strings eg ?foo=bar&fizz=buzz returns {foo: "bar", fizz: "buzz"}
 * @param {String} url
 * @returns {Object}
 */
export function getUrlVars(url = window.location) {
  const decodedUrl = decodeURIComponent(url);
  const searchParams = getCurrentQueryString(decodedUrl);
  const validParam = searchParams && searchParams.indexOf("=") !== -1;
  const isDifferentUrl = isNil(_cachedUrl) || (!isNil(_cachedUrl) && (_cachedUrl !== url));

  let urlParams = null;

  if (!validParam) {
    return {};
  } else if (isDifferentUrl) {
    urlParams = searchParams.split("&")
      .map((query) => {
        return query.indexOf("=") !== -1 ? query.split("=") : [query, true];
      })
      .reduce((result, input) => {
        const property = input[0];
        const value = input[1];
        // the explicit check on `value` !== "" is present because otherwise hasNativeDataType erroneously
        // returns true when `value` is an empty string. This, in turn, causes JSON.parse to blow up.
        const isNativeType = (value !== "") && hasNativeDataType(value);
        let val = null;

        // JSON.parse will fail when parsing values such as [abc]
        // Although [abc] is an "array string" the element within it (abc) is not a native data type and thus it fails and throws an error
        // Passing example: [\"abc\", 20, true, false]
        // Failing example: [Contact.DateHour] or [abc]
        try {
          val = isNativeType ? JSON.parse(value) : value;
        } catch (error) {
          val = value;
        }

        result[property] = removeInnerQuotes(val);
        return result;
      }, {});

    _cachedUrl = url;
    _cachedUrlParams = urlParams;

    return urlParams;
  } else {
    return cachedUrlParams();
  }
}

/**
 * @method cachedUrlParams
 * @description returns an object containing the url params
 * @param {Object} urlParams
 * @returns {Object}
 */
export function cachedUrlParams(urlParams = _cachedUrlParams) {
  return urlParams;
}

/**
 * @method getCurrentQueryString
 * @description returns everything after the ? in a url.
 * @example www.website.com/path?foo=bar&age=30 => foo=bar&age=30
 * @param {String} [url = window.location.search]
 * @returns {String}
 */
export function getCurrentQueryString(url = window.location.search) {
  return url.indexOf("?") !== -1 ? url.substr(url.indexOf("?") + 1) : null;
}

/**
 * @method constructQuery
 * @description take an object, construct into a query, e.g. {page: 1, pageSize: 10} => page=1&pageSize=10
 * @param {Object} query
 * @param {Boolean} [ignoreStringQuote=false] - Ignore adding quotes to {string} values
 * @returns {String}
 */
export function constructQuery(query, ignoreStringQuote = false) {
  return Object.keys(query).map((param) => {
    const value = query[param];
    let trueValue;
    if (Array.isArray(value)) {
      trueValue = ignoreStringQuote ? value.toString() : "[" + value.toString() + "]";
    } else if (typeof value === "string") {
      trueValue = ignoreStringQuote ? value : "\'" + value + "\'";
    } else {
      trueValue = value;
    }
    return param + "=" + trueValue;
  }).join("&");
}

/**
 * @method setUrlParam
 * @description add a new parameter to the given URL or change the value of an existing parameter
 * @param {String} url - the URL to manipulate
 * @param {String} key - name of parameter to add or change
 * @param {String} value - value of parameter to add or change
 * @returns {String} modified URL
 */
export function setUrlParam(url, key, value) {
  const base = url.split("?")[0];
  const params = (new URL(url)).searchParams;
  params.set(key, value);
  return `${base}?${params.toString()}`;
}

export function __reset__() {
  _cachedUrlParams = null;
  _cachedUrl = null;
}
